import { FC, useCallback, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { useTranslate } from 'hooks/useTranslate';
import { Notification } from 'types/Notification';

import Button from 'modules/_shared/components/Buttons';
import {
  getEquityMarketNotificationIcon,
  getEquityMarketNotificationVariant,
} from '../utils/equityMarketNotificationItem';
import { normalizeNotificationLink } from '../utils/normalizeNotificationLink';
import { Tabs } from '..';

interface Props {
  notification: Notification;
  activeTab: Tabs;
  formatDate: (date: Date | string) => string; // eslint-disable-line no-unused-vars -- callback signature in props type
  onMarkAsRead: (notificationId: string) => void; // eslint-disable-line no-unused-vars -- callback signature in props type
}

const HELP_SUFFIX = '-notif-help';

const EquityMarketNotificationsItem: FC<Props> = ({
  notification: n,
  activeTab,
  formatDate,
  onMarkAsRead,
}) => {
  const { t } = useTranslate();

  const variant = getEquityMarketNotificationVariant(n.title);
  const icon = getEquityMarketNotificationIcon(variant);
  const path = normalizeNotificationLink(n.link);
  const isUnread = n.status === 'UNREAD';
  const tooltipId = `${n._id}${HELP_SUFFIX}`;
  const equityOfferType = n.metadata?.equityMarket?.offerType;
  let offerTypeClass = '';
  if (equityOfferType === 'BUY') {
    offerTypeClass = 'equity-market-notif-offer-buy';
  } else if (equityOfferType === 'SELL') {
    offerTypeClass = 'equity-market-notif-offer-sell';
  }

  const markUnreadAsRead = useCallback(() => {
    if (!isUnread) return;
    Promise.resolve(onMarkAsRead(n._id)).catch(() => undefined);
  }, [isUnread, onMarkAsRead, n._id]);

  const handleCloseClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onMarkAsRead(n._id);
    },
    [onMarkAsRead, n._id]
  );

  return (
    <div
      className={[
        'notif-row',
        'equity-market-notif-row',
        isUnread ? 'unread' : '',
        activeTab === Tabs.CLOSED ? 'cerrado' : '',
        offerTypeClass,
      ]
        .filter(Boolean)
        .join(' ')}
      data-id={n._id}
    >
      {path ? (
        <Link
          to={path}
          className="notif-link equity-market-notif-link"
          onClick={markUnreadAsRead}
        >
          <div
            className={`notif-icon equity-market-notif-icon icon-${variant}`}
          >
            <em className={`icon ni ${icon}`} />
          </div>
          <div className="notif-body">
            <div className="notif-title">
              {n.title}
              <em
                className="icon ni ni-info help-icon ml-1 cursor-help"
                data-tooltip-id={tooltipId}
              />
              <Tooltip
                id={tooltipId}
                place="top"
                className="w-max-300px"
                style={{ zIndex: 9999 }}
              >
                {n.message}
              </Tooltip>
            </div>
            <p className="notif-desc">{n.message}</p>
            <span className="notif-date">
              {formatDate(n.date as unknown as Date)}
            </span>
          </div>
          <em className="icon ni ni-chevron-right notif-chevron" />
        </Link>
      ) : (
        <div
          className="notif-link equity-market-notif-link notif-link-static"
          onClick={markUnreadAsRead}
          role="presentation"
        >
          <div
            className={`notif-icon equity-market-notif-icon icon-${variant}`}
          >
            <em className={`icon ni ${icon}`} />
          </div>
          <div className="notif-body">
            <div className="notif-title">{n.title}</div>
            <p className="notif-desc">{n.message}</p>
            <span className="notif-date">
              {formatDate(n.date as unknown as Date)}
            </span>
          </div>
        </div>
      )}

      <div className="notif-actions equity-market-notif-actions">
        <Button
          className="btn-cerrar equity-market-notif-close"
          aria-label={t('EquityMarketNotificationsCloseAria')}
          onClick={handleCloseClick}
          style={activeTab === Tabs.CLOSED ? { display: 'none' } : undefined}
        >
          {t('EquityMarketNotificationsClose')}
        </Button>
      </div>
    </div>
  );
};

export default EquityMarketNotificationsItem;
